import type {
  BillingInterval,
  BillingPlan,
  PaymentMethod,
  StripeSubscription,
  UpdateSubscriptionPayload,
} from "@cohesive/api-client";

/** A plan with its intervals folded together — one card, one or two prices. */
export type TransformedPlan = {
  name: string;
  display_name: string;
  monthly_price?: number;
  yearly_price?: number;
  credit_price: number;
  included_credits_per_seat: number;
  available_intervals: BillingInterval[];
};

/** What a plan configuration resolves to before it is applied. */
export type PlanSelection = {
  plan: TransformedPlan;
  billing_cycle: BillingInterval;
  user_count: number;
  redirectTo?: string;
};

/**
 * Fold the API's per-interval plan rows into one entry per plan, prices in
 * dollars. Every surface that offers a plan choice reads from here.
 */
export function transformBillingPlans(
  plans: BillingPlan[] | undefined,
): TransformedPlan[] {
  if (!plans) return [];

  const byName = new Map<string, TransformedPlan>();

  for (const plan of plans) {
    if (!byName.has(plan.name)) {
      byName.set(plan.name, {
        name: plan.name,
        display_name: plan.display_name,
        credit_price: plan.credit_price,
        included_credits_per_seat: plan.included_credits_per_seat,
        available_intervals: [],
      });
    }

    const transformed = byName.get(plan.name)!;
    if (plan.billing_interval === "month") {
      transformed.monthly_price = plan.seat_price / 100;
      transformed.available_intervals.push("month");
    } else if (plan.billing_interval === "year") {
      transformed.yearly_price = plan.seat_price / 100;
      transformed.available_intervals.push("year");
    }
  }

  return Array.from(byName.values());
}

/**
 * Maximum seats a trialing subscription may hold without a payment method.
 * Growing past this ends the trial immediately (enforced server-side).
 */
export const TRIAL_MAX_SEATS = 3;

/**
 * True when applying the selection would end an in-progress trial: growing a
 * trialing subscription past TRIAL_MAX_SEATS. Matches the server rule —
 * growth only, so a subscription already above the cap isn't affected by
 * unrelated changes or seat decreases.
 */
export function selectionEndsTrial(
  selection: PlanSelection,
  subscription: StripeSubscription | null | undefined,
): boolean {
  return (
    subscription?.status === "trialing" &&
    selection.user_count > TRIAL_MAX_SEATS &&
    selection.user_count > subscription.seat_count
  );
}

/**
 * Statuses where the backend replaces the subscription with a new one instead
 * of updating it. Replacements never inherit the prior subscription's payment
 * method — the payload must carry one explicitly.
 */
export function subscriptionWillBeReplaced(
  subscription: StripeSubscription | null | undefined,
): boolean {
  return (
    !subscription ||
    subscription.status === "canceled" ||
    subscription.status === "incomplete" ||
    subscription.status === "incomplete_expired"
  );
}

/**
 * True when the customer already has an ended subscription, so resubscribing is
 * billed immediately with no free trial (the trial is first-subscription-only).
 * A first-time customer with no subscription still gets the trial.
 */
export function resubscribesWithoutTrial(
  subscription: StripeSubscription | null | undefined,
): boolean {
  return (
    subscription != null &&
    (subscription.status === "canceled" ||
      subscription.status === "incomplete" ||
      subscription.status === "incomplete_expired")
  );
}

/**
 * Decide whether a plan configuration can be submitted directly or must go
 * through checkout to collect a card first. Trialing subscriptions may change
 * seats freely up to TRIAL_MAX_SEATS without a payment method — only card
 * holders, or trial-safe seat changes, update directly.
 */
export function resolvePlanChangeAction(params: {
  selection: PlanSelection;
  subscription: StripeSubscription | null | undefined;
  hasPaymentMethod: boolean;
}): "update" | "checkout" {
  const { selection, subscription, hasPaymentMethod } = params;

  if (hasPaymentMethod) {
    return "update";
  }

  const planAndIntervalUnchanged =
    subscription != null &&
    selection.plan.name === subscription.plan_name &&
    selection.billing_cycle === subscription.billing_interval;

  if (
    subscription?.status === "trialing" &&
    planAndIntervalUnchanged &&
    selection.user_count <= TRIAL_MAX_SEATS
  ) {
    return "update";
  }

  return "checkout";
}

export type PlanChangeAction =
  | { type: "checkout" }
  | { type: "update"; payload: UpdateSubscriptionPayload };

/**
 * Resolve a plan selection into either a direct subscription update or a
 * redirect to checkout. Consolidates the payment-method lookup, the
 * update-vs-checkout decision, and the update payload so the call sites can't
 * drift: the customer's saved default is attached only when the subscription is
 * replaced, and trial_end_now is set when the change ends the trial (so the
 * optimistic cache flips to active and the server intent is explicit).
 */
export function buildPlanChangeAction(params: {
  selection: PlanSelection;
  subscription: StripeSubscription | null | undefined;
  paymentMethods: PaymentMethod[] | undefined;
}): PlanChangeAction {
  const { selection, subscription, paymentMethods } = params;

  // The subscription's default is authoritative for "card on file" — after a
  // keep_trial checkout it carries the attached card while the payment-methods
  // list can still be empty or stale. Prefer the list's default id (the source
  // a replacement payload must echo), then fall back to the subscription's.
  const defaultPaymentMethodId =
    paymentMethods?.find((pm) => pm.default)?.id ??
    subscription?.default_payment_method_id;

  const action = resolvePlanChangeAction({
    selection,
    subscription,
    hasPaymentMethod: Boolean(defaultPaymentMethodId),
  });

  if (action === "checkout") {
    return { type: "checkout" };
  }

  return {
    type: "update",
    payload: {
      plan_name: selection.plan.name,
      billing_interval: selection.billing_cycle,
      seat_count: selection.user_count,
      ...(subscriptionWillBeReplaced(subscription) && defaultPaymentMethodId
        ? { default_payment_method_id: defaultPaymentMethodId }
        : {}),
      ...(selectionEndsTrial(selection, subscription)
        ? { trial_end_now: true }
        : {}),
    },
  };
}
